export const appColorTokens = {
  app: {
    surface: {
      card: '{surface.0}',        // #ffffff
      ground: '#f0f3f8',
      muted: '#eef3f8',
      subtle: '#eef2f6',
      light: '{surface.100}',     // #f1f5f9
      gray: '#eeeeee',
      header: '#10314B',
      status: '#babcbf',
      frost: '#f0f4fa',
      haze: '#eaeaea',
      parchment: '#faf7f2'        // portal page background — lighter than the gold header tint
    },
    text: {
      heading: '#10314b',
      inverse: '{surface.0}',     // #ffffff — text on filled/dark surfaces
      secondary: '{surface.500}', // #64748b
      strong: '{surface.600}',    // #475569
      faint: '{surface.400}',     // #94a3b8
      muted: '#7c8a99',
      subtle: '#8a98a8',
      soft: '#667085',
      slate: '#6b7a8d',
      slate2: '#5b6b7c',
      slate3: '#5f6f7f',
      gray: '#606465',
      onNavy: '#cfe0ea',
      link: '#0000EE'             // Standard web link blue
    },
    border: {
      subtle: '#e8edf3',
      faint: '#f0f3f7',
      muted: '#e1e8f0',
      cool: '#e6ecf2',
      soft: '#dbe3ec',
      pale: '#d7e0ea',
      neutral: '{surface.200}',   // #e2e8f0
      gray: '#e5e7eb',
      strong: '{surface.300}'     // #cbd5e1
    },
    brand: {
      navy: '#10314b',
      navyDeep: '#16365c',
      navyAlt: '#1a3d5c',
      navySoft: '#111820',
      green: '#2e9e50',
      greenDeep: '#1a6638',
      maroon: '#612F2B'       // Divisions & Classes active tab
    },
    family: {
      accent: '#0c3f6e',      // Family portal primary - teal from sidebar gradient start
      navyMid: '#002a52',     // Navy - sidebar gradient middle
      navyDark: '#001b33',    // Dark navy - sidebar gradient end
      green: '#0db14b',       // CFC green - from logo
      blue: '#00539F'         // CFC blue - from logo
    },
    accent: {
      info: '{blue.600}',          // #2563eb
      infoSoft: '#dbe7f6',
      infoSoftHover: '#c9dcf3',
      infoStrong: '#1a5f9e',
      infoBright: '#1e7fd4',
      infoDeep: '{blue.700}',      // #1d4ed8 — hover on a filled info button
      infoTint: '{blue.100}',      // #dbeafe
      /* "Add Entry" / "Add Pass" outlined buttons on the exhibitor card, which
         are deliberately orange/sky rather than the show accent. */
      entry: '{orange.500}',       // #f97316
      entryDeep: '{orange.600}',   // #ea580c
      entrySurface: '{orange.50}', // #fff7ed
      pass: '{sky.500}',           // #0ea5e9
      passDeep: '{sky.600}',       // #0284c7
      passSurface: '{sky.50}'      // #f0f9ff
    },

    /* Neutral ramp (Tailwind `gray`, distinct from the slate-based `surface`
       scale). Several components predate the semantic text/border/surface
       tokens and were built against these literals; holding them as tokens
       retires the literals without shifting a pixel. Prefer app.text /
       app.border / app.surface for new work. */
    neutral: {
      50: '{gray.50}',    // #f9fafb
      100: '{gray.100}',  // #f3f4f6
      300: '{gray.300}',  // #d1d5db
      400: '{gray.400}',  // #9ca3af
      500: '{gray.500}',  // #6b7280
      600: '{gray.600}',  // #4b5563
      700: '{gray.700}',  // #374151
      800: '{gray.800}',  // #1f2937
      900: '{gray.900}',  // #111827
      subtle: '{slate.50}', // #f8fafc
      stripe: '{zinc.50}'   // #fafafa — zebra rows
    },
    status: {
      success: '{green.600}',          // #16a34a
      successBright: '{green.500}',    // #22c55e
      successDeep: '{green.700}',      // #15803d
      successAlt: '#198754',
      successAlt2: '#2fa84f',
      successSurface: '{green.100}',   // #dcfce7
      successTint: '#eaf7ee',
      successTint2: '#eaf5f0',
      successTint3: '#f4faf7',
      successTint4: '#f4f8f5',
      warning: '{amber.500}',          // #f59e0b
      warnText: '{orange.700}',        // #c2410c
      warnSurface: '{orange.100}',     // #ffedd5
      danger: '{red.500}',             // #ef4444
      dangerDeep: '{red.600}',         // #dc2626
      dangerText: '{red.700}',         // #b91c1c
      dangerSurface: '{red.100}',      // #fee2e2
      infoText: '{sky.700}',           // #0369a1
      infoSurface: '{sky.100}',        // #e0f2fe
      infoInk: '{sky.900}',            // #0c4a6e — info text on its own surface
      successEmerald: '{emerald.600}', // #059669 — completed step / check marks
      warnDeep: '{amber.600}',         // #d97706
      warnStrong: '{amber.800}',       // #92400e — warning text on a tint
      warnTint: '{amber.100}',         // #fef3c7
      warnBorder: '{amber.300}',       // #fcd34d
      /* Warn badge: gold.bright/strong as literals, so a show's gold override can't recolour a status. */
      warnBadge: '#d4780a',
      warnBadgeText: '#a05800',
      dangerStrong: '{red.800}',       // #991b1b
      /* Danger text legible on a dark sidebar, where dangerText is too dim. */
      dangerOnDark: '#ff9c9c'
    },
    gold: {
      primary: '#f5a623',     // entry/pass form + wizard accent (educator + family)
      step: '#ffa726',        // stepper active-step circle
      base: '#b8770f',
      bright: '#d4780a',
      strong: '#a05800',
      muted: '#a07a3d',
      surface: '#fbf3e6',
      border: '#f1e1c4',
      highlight: '#f5b331',   // portal sidebar active-item highlight
      glow: '#d9a75a'         // focus ring around the active wizard step
    },

    /* Show-picker cards (family portal). A self-contained blue selection scale;
       it is not the show accent, which varies per show. */
    pickShow: {
      surface: '#f0f7ff',
      selected: '#dff1ff',
      hover: '#cce5ff',
      active: '#b3d9ff',
      primary: '#1976d2',
      ink: '#1a1a1a'
    },
    shadow: '#000000',
    scrim: '#081e30',

    /* Avatar tints (palette-derived where possible). */
    avatar: {
      indigo: '{indigo.500}',      // #6366f1
      indigoDeep: '{indigo.600}',  // #4f46e5
      teal: '{teal.500}',          // #14b8a6
      blue: '{blue.500}',          // #3b82f6
      green: '{green.500}',        // #22c55e
      pink: '{pink.500}',          // #ec4899
      cyan: '{cyan.600}'           // #0891b2
    },

    /* Per-show brand colours (the "preset per show"). `sidebar` skins the
       portal left-menu; falls back to `gradient` when a show omits it.
       `tint` is the light logo band (Pick a Show card, landing spotlight). */
    show: {
      sa:    { accent: '#4A3726', card: '#8b1a1a', gradient: 'linear-gradient(135deg, #261E1E 0%, #441619 60%, #541D20 100%)', sidebar: 'linear-gradient(180deg, #441619 0%, #541D20 28.41%, #4A3726 72.9%, #261E1E 100%)', tint: 'linear-gradient(160deg, #fff8ef 0%, #f8dcb0 55%, #f0c4b6 100%)' },
      fourh: { accent: '#2d5016', card: '#2d5016', gradient: 'linear-gradient(135deg, #1a2d0f 0%, #2d5016 60%, #3d7022 100%)', tint: 'linear-gradient(160deg, #f5fbf6 0%, #d3f0dc 55%, #bfe8cd 100%)' },
      hdgs:  { accent: '#5a2a8a', card: '#5a2a8a', gradient: 'linear-gradient(135deg, #1a0a2d 0%, #3d1a5c 60%, #5a2a8a 100%)', tint: 'linear-gradient(160deg, #ffffff 0%, #f5eefc 55%, #ede0f8 100%)' }
    },

    /* Show-admin counterparts. SA uses navy gradient similar to family portal
       but with independent color values that can be adjusted separately. The rest
       are the `show` brand family above, deepened and desaturated so the back
       office reads as a distinct surface. Every show sets `sidebar` explicitly —
       the admin chrome shouldn't silently inherit the 135deg hero gradient. */
    showAdmin: {
      sa:    { accent: '#0c3f6e', card: '#00539F', gradient: 'linear-gradient(135deg, #001b33 0%, #002a52 60%, #0c3f6e 100%)', sidebar: 'linear-gradient(180deg, #0c3f6e 0%, #002a52 45%, #001b33 100%)' },
      fourh: { accent: '#1F3810', card: '#1F3810', gradient: 'linear-gradient(135deg, #101B09 0%, #1F3810 60%, #294D17 100%)', sidebar: 'linear-gradient(180deg, #12200A 0%, #1F3810 28.41%, #294D17 72.9%, #101B09 100%)' },
      hdgs:  { accent: '#3F1D61', card: '#3F1D61', gradient: 'linear-gradient(135deg, #120720 0%, #2A1240 60%, #3F1D61 100%)', sidebar: 'linear-gradient(180deg, #120720 0%, #2A1240 28.41%, #3F1D61 72.9%, #0C0417 100%)' }
    },

    /* Landing-page show tiles + portal selector. */
    landing: {
      show1: 'linear-gradient(135deg,#163d0f,#4d8f1f)',
      show2: 'linear-gradient(135deg,#2d0b45,#6b21a8)',
      show3: 'linear-gradient(135deg,#5f0000,#d71920)',
      portalGreen: '#2fa84f',
      portalBlue: '#4a90e2',
      portalPurple: '#8b5cf6'
    }
  }
};
